import { Button, Stack } from '@chakra-ui/react'
import { faCheck, faHand } from '@fortawesome/pro-solid-svg-icons'
import { useEffect, useState } from 'react'

import { useUser } from '@/client/context/user'
import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import RPC from '@/client/rpc'
import hasUserAuthScope from '@/client/services/user/hasUserAuthScope'
import type { PostStatus } from '@/client/services/worlds/types'

import { useToaster } from '@/components/Toast/useToaster'
import Icon from '@/components/UI/Icon/Icon'
import type { LiveHelpRequestBase } from '@/components/Worlds/types'

import PostStatusTags from './PostStatusTags'

export default function PostStatusSection({
  postId,
  postUserId,
  status,
  liveHelpRequest,
}: {
  postId: number
  postUserId: number
  status: PostStatus
  liveHelpRequest: LiveHelpRequestBase | null
}): JSX.Element {
  const toast = useToaster()
  const currentUser = useUser()

  const [optimisticPostStatus, setOptimisticPostStatus] = useState(status)

  const canUpdateStatus =
    currentUser.id === postUserId || hasUserAuthScope(currentUser, 'admin:posts:edit')

  useEffect(() => setOptimisticPostStatus(status), [status])

  const [updatePostStatus, isPostStatusUpdating] = useAPIWrapper(
    async (newStatus: PostStatus) => {
      setOptimisticPostStatus(newStatus)

      try {
        await RPC.updatePostStatus({ postId, status: newStatus })
        toast({
          title:
            newStatus === 'solved'
              ? 'Nice! You marked this question as solved'
              : 'You marked this question as not solved',
          status: 'success',
        })
      } catch (err) {
        setOptimisticPostStatus(status)
        throw err
      }
    },
    [postId, toast, status],
    'Update Post Status'
  )

  return (
    <Stack direction="row" alignItems="center">
      {canUpdateStatus ? (
        optimisticPostStatus === 'not-solved' ? (
          <Button
            variant="outline"
            onClick={() => updatePostStatus('solved')}
            leftIcon={<Icon faIcon={faCheck} size="md" />}
            isDisabled={isPostStatusUpdating}
          >
            Mark as Solved
          </Button>
        ) : (
          <Button
            variant="outline"
            onClick={() => updatePostStatus('not-solved')}
            leftIcon={<Icon faIcon={faHand} size="md" />}
            isDisabled={isPostStatusUpdating}
          >
            Mark as Not Solved
          </Button>
        )
      ) : null}
      <PostStatusTags status={optimisticPostStatus} currentLiveHelpRequest={liveHelpRequest} />
    </Stack>
  )
}
